import React from 'react';
import { Box, Grid, Paper, Stack, Tooltip, Typography } from '@mui/material';
import {
  AccountBalanceWallet,
  EventBusy,
  ShowChart,
  ShoppingCart,
  TrendingDown,
  TrendingFlat,
  TrendingUp,
} from '@mui/icons-material';
import { formatCurrency } from '../../../../utils/formatters';
import type { Info360Indicadores } from '../types';

interface Props {
  data: Info360Indicadores;
}

interface KpiItem {
  title: string;
  value: string;
  helper: string;
  icon: React.ReactNode;
  tone: 'ok' | 'warning' | 'danger' | 'neutral';
  tooltip: string;
}

const toneColor = (tone: KpiItem['tone']): string => {
  if (tone === 'ok') return '#2e7d32';
  if (tone === 'warning') return '#ed6c02';
  if (tone === 'danger') return '#d32f2f';
  return '#455a64';
};

const Info360KpiRow: React.FC<Props> = ({ data }) => {
  const creditoTone: KpiItem['tone'] =
    data.creditoDisponible < 0 ? 'danger'
      : data.creditoDisponible < data.creditoLimite * 0.15 ? 'warning'
        : 'ok';

  const deudaTone: KpiItem['tone'] =
    data.saldoVencido <= 0 ? 'ok'
      : data.saldoVencido > data.creditoLimite * 0.2 ? 'danger'
        : 'warning';

  const tendenciaIcon =
    data.tendenciaVentasPct != null && data.tendenciaVentasPct > 3
      ? <TrendingUp fontSize="small" aria-hidden />
      : data.tendenciaVentasPct != null && data.tendenciaVentasPct < -3
        ? <TrendingDown fontSize="small" aria-hidden />
        : <TrendingFlat fontSize="small" aria-hidden />;

  const compraTone: KpiItem['tone'] =
    data.diasDesdeUltimaCompra == null ? 'neutral'
      : data.diasDesdeUltimaCompra > 90 ? 'danger'
        : data.diasDesdeUltimaCompra > 60 ? 'warning'
        : 'ok';

  const items: KpiItem[] = [
    {
      title: 'Crédito disponible',
      value: formatCurrency(data.creditoDisponible),
      helper: creditoTone === 'ok' ? 'Disponible' : creditoTone === 'warning' ? 'Bajo' : 'Excedido',
      icon: <AccountBalanceWallet fontSize="small" aria-hidden />,
      tone: creditoTone,
      tooltip: `Límite ${formatCurrency(data.creditoLimite)} · Utilizado ${formatCurrency(data.creditoUtilizado)}`,
    },
    {
      title: 'Deuda vencida',
      value: formatCurrency(data.saldoVencido),
      helper: deudaTone === 'ok' ? 'Sin mora' : deudaTone === 'warning' ? 'Con mora' : 'Mora alta',
      icon: <EventBusy fontSize="small" aria-hidden />,
      tone: deudaTone,
      tooltip: `Saldo total ${formatCurrency(data.saldoTotal)} · Vigente ${formatCurrency(data.saldoVigente)}`,
    },
    {
      title: 'Ventas 12 meses',
      value: formatCurrency(data.ventas12Meses),
      helper: data.tendenciaVentasPct == null
        ? 'Sin período comparable'
        : `${data.tendenciaVentasPct > 0 ? '+' : ''}${data.tendenciaVentasPct}% tendencia`,
      icon: <ShowChart fontSize="small" aria-hidden />,
      tone: data.tendenciaVentasPct != null && data.tendenciaVentasPct < -15 ? 'warning' : 'neutral',
      tooltip: 'Variación respecto de los 12 meses anteriores.',
    },
    {
      title: 'Última compra',
      value: data.diasDesdeUltimaCompra == null
        ? 'Sin compras'
        : data.diasDesdeUltimaCompra === 0 ? 'Hoy' : `${data.diasDesdeUltimaCompra} días`,
      helper: data.diasDesdeUltimaCompra == null
        ? 'Sin historial'
        : compraTone === 'ok' ? 'Activo' : 'Poco frecuente',
      icon: <ShoppingCart fontSize="small" aria-hidden />,
      tone: compraTone,
      tooltip: 'Días transcurridos desde la última factura emitida.',
    },
  ];

  return (
    <Grid container spacing={1.5}>
      {items.map((item) => (
        <Grid item xs={12} sm={6} md={3} key={item.title}>
          <Tooltip title={item.tooltip} arrow>
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                borderRadius: 2,
                height: '100%',
                borderColor: toneColor(item.tone),
                borderWidth: 1,
              }}
            >
              <Stack spacing={0.75}>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Box sx={{ color: toneColor(item.tone), display: 'flex' }}>{item.icon}</Box>
                  <Typography variant="caption" color="text.secondary">
                    {item.title}
                  </Typography>
                </Stack>
                <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                  {item.value}
                </Typography>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  {item.title === 'Ventas 12 meses' ? (
                    <Box sx={{ color: toneColor(item.tone), display: 'flex' }}>{tendenciaIcon}</Box>
                  ) : null}
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    sx={{ color: toneColor(item.tone) }}
                  >
                    {item.helper}
                  </Typography>
                </Stack>
              </Stack>
            </Paper>
          </Tooltip>
        </Grid>
      ))}
    </Grid>
  );
};

export default Info360KpiRow;
